"use client";

import type { BapAccountBackup, BapMasterBackup } from "bitcoin-backup";
import { encryptBackup } from "bitcoin-backup";
import {
	Check,
	ChevronDown,
	Download,
	Loader2,
	Plus,
	RefreshCw,
	Settings,
	User,
} from "lucide-react";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import Avatar from "sigma-avatars";
import { toast } from "sonner";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useIdentity } from "@/contexts/identity-context";
import { useBapProfiles } from "@/hooks/use-bap-profile";
import { useCreateIdentity } from "@/hooks/use-create-identity";
import type { SessionUser } from "@/hooks/use-session";
import { readLocalDraftProfile } from "@/lib/bap/backup";
import type { BAPProfile, ResolvedIdentity } from "@/lib/bap/core";
import {
	getAccountIdentity,
	importDiscoveredIdentity,
	saveUpdatedBackup,
	syncIdentitiesFromChain,
} from "@/lib/bap/core";
import {
	getDisplayName,
	getMergedProfile,
	mergeProfileChanges,
	truncateId,
} from "@/lib/bap/display";
import { downloadEncryptedBackup, downloadFile } from "@/lib/bap/utils";
import { DISPLAY_IMAGE, getDisplayImageUrl } from "@/lib/image-utils";
import { getEncryptMemberBackupsSetting } from "@/lib/settings";
import { STORAGE_KEYS } from "@/lib/storage-keys";
import OptimizedImage from "./optimized-image";

/** Render the avatar for the dropdown trigger button */
function renderTriggerAvatar(
	avatarUrl: string | null,
	currentIdentity: ResolvedIdentity | null
): React.ReactNode {
	if (avatarUrl) {
		return (
			<OptimizedImage
				alt="Profile avatar"
				className="h-5 w-5 rounded-full object-cover"
				height={20}
				src={avatarUrl}
				width={20}
			/>
		);
	}
	if (currentIdentity) {
		return (
			<Avatar
				colors={[
					"var(--chart-1)",
					"var(--chart-2)",
					"var(--chart-3)",
					"var(--chart-4)",
					"var(--chart-5)",
				]}
				name={currentIdentity.bapId}
				size={20}
				variant="bauhaus"
			/>
		);
	}
	return <User className="h-4 w-4" />;
}

/** Extract a BapAccountBackup from a master backup for a specific identity */
function extractMemberKey(bapId: string): BapAccountBackup | null {
	const member = getAccountIdentity(bapId);
	if (!member) {
		toast.error("Failed to get member key");
		return null;
	}
	return { wif: member.getAccountKey().toWif(), id: member.bapId };
}

/** Build the navigation path when switching identities */
function buildIdentityPath(pathname: string, bapId: string): string {
	if (pathname.startsWith("/account/")) {
		const pathParts = pathname.split("/");
		const restOfPath = pathParts.slice(3).join("/");
		if (restOfPath) {
			return `/account/${encodeURIComponent(bapId)}/${restOfPath}`;
		}
	}
	return `/account/${encodeURIComponent(bapId)}`;
}

/** Navigate to an identity page then reload */
function navigateAndReload(
	routerPush: (path: string) => void,
	identityId: string | null,
	delay = 100
): void {
	if (identityId) {
		routerPush(`/account/${encodeURIComponent(identityId)}`);
		setTimeout(() => window.location.reload(), delay);
	} else {
		setTimeout(() => window.location.reload(), delay > 100 ? delay : 300);
	}
}

/** Build the account navigation href for a given sub-path */
function buildAccountHref(
	currentIdentity: ResolvedIdentity | null,
	subPath?: string
): string {
	if (!currentIdentity) {
		return "/account";
	}
	const base = `/account/${encodeURIComponent(currentIdentity.bapId)}`;
	return subPath ? `${base}/${subPath}` : base;
}

function parseDraftProfile(raw: string | null): Partial<BAPProfile> | null {
	if (!raw) {
		return null;
	}
	try {
		return JSON.parse(raw) as Partial<BAPProfile>;
	} catch {
		return null;
	}
}

function applyDraftProfile(
	profile: BAPProfile | null,
	draftProfile: Partial<BAPProfile> | null
): BAPProfile | null {
	if (!draftProfile) {
		return profile;
	}
	return mergeProfileChanges(profile, draftProfile, {}) as BAPProfile;
}

function subscribeSeedCapability(listener: () => void) {
	window.addEventListener("bap-backup-updated", listener);
	window.addEventListener("storage", listener);
	return () => {
		window.removeEventListener("bap-backup-updated", listener);
		window.removeEventListener("storage", listener);
	};
}
function seedCapabilitySnapshot(): "legacy" | "seed" | "partial" {
	try {
		const backup = JSON.parse(
			sessionStorage.getItem(STORAGE_KEYS.DECRYPTED_BACKUP) || "null"
		);
		if (backup?.format !== "sigma-seed") {
			return "legacy";
		}
		return backup.inventoryComplete === false ? "partial" : "seed";
	} catch {
		return "legacy";
	}
}

const capabilityText = {
	legacy: {
		discover: "Discover identities",
		create: "Create identity",
		note: "",
	},
	seed: {
		discover: "Import your complete backup file to restore seed profiles",
		create: "Create identity",
		note: "Restore seed profile inventory from your complete backup file; automatic discovery is unavailable.",
	},
	partial: {
		discover: "Import your complete backup file to restore seed profiles",
		create: "Import your complete backup file before creating another profile",
		note: "Import your complete backup file before adding or removing profiles.",
	},
};
interface UserIdentityMenuProps {
	user: SessionUser | null;
	onSignOut: () => void;
}

export function UserIdentityMenu({ user, onSignOut }: UserIdentityMenuProps) {
	const router = useRouter();
	const seedCapability = useSyncExternalStore<"legacy" | "seed" | "partial">(
		subscribeSeedCapability,
		seedCapabilitySnapshot,
		() => "legacy"
	);
	const pathname = usePathname();
	const { identities, currentIdentity, currentProfile, setCurrentIdentity } =
		useIdentity();
	const profileQueries = useBapProfiles(identities.map((id) => id.bapId));
	const { createIdentity } = useCreateIdentity();
	const [discoverDialogOpen, setDiscoverDialogOpen] = useState(false);
	const [discovering, setDiscovering] = useState(false);
	const [discoveredIdentities, setDiscoveredIdentities] = useState<
		ResolvedIdentity[]
	>([]);
	const [createDialogOpen, setCreateDialogOpen] = useState(false);
	const [newIdentityName, setNewIdentityName] = useState("");
	const [newIdentityPassword, setNewIdentityPassword] = useState("");
	const [creating, setCreating] = useState(false);
	const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
	const [password, setPassword] = useState("");
	const [pendingIdentity, setPendingIdentity] =
		useState<ResolvedIdentity | null>(null);
	const [importing, setImporting] = useState(false);

	// State for backup download confirmation after identity creation
	const [confirmBackupDownloadOpen, setConfirmBackupDownloadOpen] =
		useState(false);
	const [newlyCreatedIdentityName, setNewlyCreatedIdentityName] = useState("");
	const [newlyCreatedIdentityId, setNewlyCreatedIdentityId] = useState<
		string | null
	>(null);

	// State for master backup password dialog
	const [masterPasswordDialogOpen, setMasterPasswordDialogOpen] =
		useState(false);
	const [masterPassword, setMasterPassword] = useState("");
	const [downloadingMaster, setDownloadingMaster] = useState(false);
	const [shouldReloadAfterDownload, setShouldReloadAfterDownload] =
		useState(false);

	// State for member backup password dialog
	const [memberPasswordDialogOpen, setMemberPasswordDialogOpen] =
		useState(false);
	const [memberPassword, setMemberPassword] = useState("");
	const [downloadingMember, setDownloadingMember] = useState(false);

	// Track localStorage changes to trigger re-renders
	const [_storageVersion, setStorageVersion] = useState(0);

	// Listen for draft profile updates (custom event works in same window)
	useEffect(() => {
		const handleDraftUpdate = () => {
			setStorageVersion((v) => v + 1);
		};

		window.addEventListener("bap-draft-updated", handleDraftUpdate);
		return () =>
			window.removeEventListener("bap-draft-updated", handleDraftUpdate);
	}, []);

	const draftProfilesById = Object.fromEntries(
		identities.map((identity) => [
			identity.bapId,
			parseDraftProfile(readLocalDraftProfile(identity.bapId)),
		])
	) as Record<string, Partial<BAPProfile> | null>;

	const activeProfile = useMemo(() => {
		const baseProfile = getMergedProfile(currentIdentity, currentProfile);
		const activeDraft = currentIdentity
			? draftProfilesById[currentIdentity.bapId]
			: null;
		return applyDraftProfile(baseProfile, activeDraft);
	}, [currentIdentity, currentProfile, draftProfilesById]);

	const displayName = useMemo(
		() =>
			getDisplayName(
				activeProfile,
				currentIdentity,
				user?.name || user?.email || "Account"
			),
		[activeProfile, currentIdentity, user]
	);

	const avatarUrl = useMemo(() => {
		const profileImage = activeProfile?.identity?.image?.trim();

		if (profileImage) {
			return getDisplayImageUrl(profileImage, null, DISPLAY_IMAGE.avatar);
		}

		// No image - return null to use sigma-avatars fallback
		return null;
	}, [activeProfile]);

	const handleDiscoverIdentities = async () => {
		if (seedCapabilitySnapshot() !== "legacy") {
			toast.info(
				"Import your complete backup file to restore your seed profile inventory."
			);
			return;
		}
		setDiscovering(true);
		const discoverToast = toast.loading(
			"Scanning blockchain for identities..."
		);

		try {
			// Use sync function instead of discover
			const newCount = await syncIdentitiesFromChain();

			if (newCount > 0) {
				toast.success(`Found and imported ${newCount} new identities!`, {
					id: discoverToast,
					description: "Your backup has been updated with the new identities.",
					duration: 5000,
				});
				// Reload to refresh identity list
				window.location.reload();
			} else {
				toast.info("No new identities found on-chain", {
					id: discoverToast,
					description:
						"All your on-chain identities are already in your backup.",
					duration: 5000,
				});
			}
		} catch {
			toast.error("Failed to discover identities", {
				id: discoverToast,
				description: "Please try again.",
				duration: 6000,
			});
		} finally {
			setDiscovering(false);
		}
	};

	const handleImportIdentity = (identity: ResolvedIdentity) => {
		// Store the identity and open password dialog
		setPendingIdentity(identity);
		setPasswordDialogOpen(true);
	};

	const handleConfirmImport = async () => {
		if (seedCapabilitySnapshot() !== "legacy") {
			toast.error(
				"Seed profiles must be restored from their backup file or exact recovery index."
			);
			return;
		}
		if (!pendingIdentity) {
			return;
		}

		setImporting(true);
		const importToast = toast.loading("Importing identity...");

		try {
			const result = importDiscoveredIdentity(
				pendingIdentity.counter,
				pendingIdentity.name
			);

			if (!result) {
				toast.error("Failed to import identity", {
					id: importToast,
					description: "Only master backups can import identities.",
				});
				return;
			}

			// Update backup in sessionStorage and localStorage
			// Pass password to enable localStorage persistence
			await saveUpdatedBackup(result.updatedBackup, password || undefined);

			// Remove from discovered list
			setDiscoveredIdentities((prev) =>
				prev.filter((id) => id.bapId !== pendingIdentity.bapId)
			);

			toast.success("Identity imported successfully!", {
				id: importToast,
				description: password
					? `"${pendingIdentity.name}" saved to backup and will persist across sessions`
					: `"${pendingIdentity.name}" imported (session only - provide password to save permanently)`,
			});

			// Navigate to the newly imported identity
			router.push(`/account/${encodeURIComponent(pendingIdentity.bapId)}`);

			// Close dialogs
			setPasswordDialogOpen(false);
			setPassword("");
			setPendingIdentity(null);

			// Close discovery dialog if no more identities to import
			if (discoveredIdentities.length <= 1) {
				setDiscoverDialogOpen(false);
			}

			// Trigger a page reload to refresh the identity list
			window.location.reload();
		} catch {
			toast.error("Failed to import identity", {
				id: importToast,
				description: "Please check your backup and try again.",
			});
		} finally {
			setImporting(false);
		}
	};

	const handleCreateNewIdentity = async () => {
		if (!newIdentityName.trim()) {
			toast.error("Please enter an identity name");
			return;
		}

		if (!newIdentityPassword) {
			toast.error("Password is required to update backup");
			return;
		}

		setCreating(true);
		const createToast = toast.loading("Validating password...");

		try {
			toast.loading("Creating new identity...", { id: createToast });

			const newIdKey = await createIdentity(
				newIdentityName,
				newIdentityPassword
			);

			toast.success("Identity created and backup updated!", {
				id: createToast,
				description: `"${newIdentityName}" has been saved to your encrypted backup.`,
				duration: 8000,
			});

			localStorage.setItem(STORAGE_KEYS.CURRENT_IDENTITY, newIdKey);

			setNewIdentityName("");
			setNewIdentityPassword("");
			setCreateDialogOpen(false);

			setNewlyCreatedIdentityName(newIdentityName);
			setNewlyCreatedIdentityId(newIdKey);
			setConfirmBackupDownloadOpen(true);
		} catch {
			toast.error("Failed to create identity", {
				id: createToast,
				description: "Please check your password and try again.",
			});
		} finally {
			setCreating(false);
		}
	};

	const downloadMasterBackup = () => {
		try {
			const backupJson = sessionStorage.getItem(STORAGE_KEYS.DECRYPTED_BACKUP);
			if (!backupJson) {
				toast.error(
					"No backup found in session. Please unlock your backup first."
				);
				return;
			}

			// Open password dialog
			setMasterPasswordDialogOpen(true);
		} catch {
			toast.error("Failed to prepare backup download");
		}
	};

	const handleDownloadMasterBackup = async () => {
		if (!masterPassword) {
			toast.error("Please enter a password");
			return;
		}

		setDownloadingMaster(true);
		try {
			const backupJson = sessionStorage.getItem(STORAGE_KEYS.DECRYPTED_BACKUP);
			if (!backupJson) {
				toast.error("No backup found in session.");
				return;
			}

			const backup: BapMasterBackup = JSON.parse(backupJson);
			await downloadEncryptedBackup(backup, masterPassword);
			toast.success("Master backup downloaded successfully!");

			// Close dialog and reset
			setMasterPasswordDialogOpen(false);
			setMasterPassword("");

			if (shouldReloadAfterDownload) {
				setShouldReloadAfterDownload(false);
				navigateAndReload(router.push, newlyCreatedIdentityId);
			}
		} catch {
			toast.error("Failed to download backup. Please try again.");
		} finally {
			setDownloadingMaster(false);
		}
	};

	const handleDownloadMemberBackup = async () => {
		if (!(currentIdentity && memberPassword)) {
			toast.error("Please enter a password");
			return;
		}

		setDownloadingMember(true);
		try {
			const backupJson = sessionStorage.getItem(STORAGE_KEYS.DECRYPTED_BACKUP);
			if (!backupJson) {
				toast.error("No backup found in session.");
				return;
			}

			const memberBackup = extractMemberKey(currentIdentity.bapId);
			if (!memberBackup) {
				return;
			}

			const encrypted = await encryptBackup(memberBackup, memberPassword);
			const filename = `${currentIdentity.name.replace(/[^a-z0-9]/gi, "_").toLowerCase()}_member.bep`;
			downloadFile(encrypted, filename, "text/plain");
			toast.success("Encrypted member backup downloaded successfully!");

			setMemberPasswordDialogOpen(false);
			setMemberPassword("");
		} catch {
			toast.error("Failed to download member backup. Please try again.");
		} finally {
			setDownloadingMember(false);
		}
	};

	const handleConfirmBackupDownload = () => {
		// Close the confirmation dialog and open password dialog
		setConfirmBackupDownloadOpen(false);
		setShouldReloadAfterDownload(true); // Flag to reload after successful download
		downloadMasterBackup();
	};

	const handleSkipBackupDownload = () => {
		setConfirmBackupDownloadOpen(false);

		// Navigate to the newly created identity
		if (newlyCreatedIdentityId) {
			router.push(`/account/${encodeURIComponent(newlyCreatedIdentityId)}`);
			// Give router time to navigate before reloading
			setTimeout(() => {
				window.location.reload();
			}, 100);
		} else {
			// Fallback if ID not set
			window.location.reload();
		}
	};

	const downloadMemberBackup = () => {
		if (!currentIdentity) {
			toast.error("No identity selected");
			return;
		}

		try {
			const backupJson = sessionStorage.getItem(STORAGE_KEYS.DECRYPTED_BACKUP);
			if (!backupJson) {
				toast.error("Please unlock your BAP backup first");
				return;
			}

			const memberBackup = extractMemberKey(currentIdentity.bapId);
			if (!memberBackup) {
				return;
			}

			if (getEncryptMemberBackupsSetting()) {
				setMemberPasswordDialogOpen(true);
			} else {
				const filename = `${currentIdentity.name.replace(/[^a-z0-9]/gi, "_").toLowerCase()}_member.txt`;
				downloadFile(JSON.stringify(memberBackup), filename, "text/plain");
				toast.success("Member backup downloaded successfully!");
			}
		} catch {
			toast.error("Failed to download member backup. Please try again.");
		}
	};

	if (!user) {
		return null;
	}

	return (
		<>
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<Button className="gap-2" size="sm" variant="outline">
						{renderTriggerAvatar(avatarUrl, currentIdentity)}
						<span className="max-w-[120px] truncate">{displayName}</span>
						<ChevronDown className="h-3 w-3 opacity-50" />
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align="end" className="w-[280px]">
					{/* Identity Section */}
					{identities.length > 0 && (
						<>
							<div className="flex items-center justify-between px-2 py-1.5">
								<DropdownMenuLabel className="p-0">
									Identities
								</DropdownMenuLabel>
								<div className="flex items-center gap-1">
									<Button
										aria-label="Discover identities"
										className="h-8 w-8"
										disabled={discovering || seedCapability !== "legacy"}
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											handleDiscoverIdentities();
										}}
										size="icon"
										title={capabilityText[seedCapability].discover}
										variant="ghost"
									>
										{discovering ? (
											<Loader2 className="h-4 w-4 animate-spin" />
										) : (
											<RefreshCw className="h-4 w-4" />
										)}
									</Button>
									<Button
										aria-label="Create identity"
										className="h-8 w-8"
										disabled={seedCapability === "partial"}
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											if (seedCapabilitySnapshot() !== "partial") {
												setCreateDialogOpen(true);
											}
										}}
										size="icon"
										title={capabilityText[seedCapability].create}
										variant="ghost"
									>
										<Plus className="h-4 w-4" />
									</Button>
								</div>
							</div>
							{seedCapability !== "legacy" && (
								<p className="px-2 py-1 text-muted-foreground text-xs">
									{capabilityText[seedCapability].note}
								</p>
							)}
							<DropdownMenuSeparator />
							<DropdownMenuGroup>
								{identities.map((identity, index) => {
									const isActive = identity.bapId === currentIdentity?.bapId;
									const fetchedProfile = profileQueries[index]?.data ?? null;

									// Get merged profile using priority: profile (KV draft > on-chain) > fetched/currentProfile
									const profile = applyDraftProfile(
										getMergedProfile(
											identity,
											isActive ? currentProfile : fetchedProfile
										),
										draftProfilesById[identity.bapId]
									);
									const profileImage = profile?.identity?.image?.trim();
									const imageUrl = profileImage
										? getDisplayImageUrl(
												profileImage,
												null,
												DISPLAY_IMAGE.avatarMenu
											)
										: null;

									return (
										<DropdownMenuItem
											key={identity.bapId}
											onClick={() => {
												setCurrentIdentity(identity.bapId);
												router.push(
													buildIdentityPath(pathname, identity.bapId)
												);
											}}
										>
											<div className="flex w-full items-center gap-3">
												{imageUrl ? (
													<OptimizedImage
														alt={`${getDisplayName(null, identity)} avatar`}
														className="h-8 w-8 rounded-full object-cover"
														height={32}
														src={imageUrl}
														width={32}
													/>
												) : (
													<Avatar
														colors={[
															"var(--chart-1)",
															"var(--chart-2)",
															"var(--chart-3)",
															"var(--chart-4)",
															"var(--chart-5)",
														]}
														name={identity.bapId}
														size={32}
														variant="bauhaus"
													/>
												)}
												<div className="flex min-w-0 flex-1 flex-col gap-0.5">
													<span className="font-medium">
														{getDisplayName(profile, identity)}
													</span>
													<span className="font-mono text-muted-foreground text-xs">
														{truncateId(identity.bapId)}
													</span>
												</div>
												{isActive && (
													<Check className="h-4 w-4 flex-shrink-0" />
												)}
											</div>
										</DropdownMenuItem>
									);
								})}
							</DropdownMenuGroup>
							<DropdownMenuSeparator />
							<DropdownMenuGroup>
								<DropdownMenuItem onClick={downloadMasterBackup}>
									<Download className="mr-2 h-4 w-4" />
									Master Backup
								</DropdownMenuItem>
								<DropdownMenuItem onClick={downloadMemberBackup}>
									<Download className="mr-2 h-4 w-4" />
									Member Backup
								</DropdownMenuItem>
							</DropdownMenuGroup>
							<DropdownMenuSeparator />
						</>
					)}

					{/* Settings lives here so the top bar stays uncluttered */}
					<DropdownMenuItem asChild>
						<a
							className="flex items-center"
							href={buildAccountHref(currentIdentity, "settings")}
						>
							<Settings className="mr-2 h-4 w-4" />
							Settings
						</a>
					</DropdownMenuItem>
					<DropdownMenuSeparator />

					{/* Sign Out */}
					<DropdownMenuItem onClick={onSignOut}>Sign Out</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>

			{/* Discovery Dialog */}
			<Dialog onOpenChange={setDiscoverDialogOpen} open={discoverDialogOpen}>
				<DialogContent className="max-w-2xl">
					<DialogHeader>
						<DialogTitle>Discovered On-Chain Identities</DialogTitle>
						<DialogDescription>
							Click an identity to import it into your backup and switch to it.
						</DialogDescription>
					</DialogHeader>

					<div className="max-h-96 space-y-3 overflow-y-auto">
						{discoveredIdentities.length === 0 ? (
							<p className="py-8 text-center text-muted-foreground">
								No new identities found.
							</p>
						) : (
							discoveredIdentities.map((identity) => (
								<Card
									className="cursor-pointer transition-colors hover:bg-muted/50"
									key={identity.bapId}
									onClick={() => handleImportIdentity(identity)}
								>
									<CardContent className="p-4">
										<div className="flex items-start gap-4">
											<Avatar
												colors={[
													"var(--chart-1)",
													"var(--chart-2)",
													"var(--chart-3)",
													"var(--chart-4)",
													"var(--chart-5)",
												]}
												name={identity.bapId}
												size={48}
												variant="bauhaus"
											/>
											<div className="min-w-0 flex-1">
												<div className="flex items-center gap-2">
													<h4 className="font-semibold">
														{getDisplayName(identity.profile || null, identity)}
													</h4>
													<span className="rounded-full bg-primary/10 px-2 py-0.5 text-primary text-xs">
														Counter {identity.counter}
													</span>
												</div>
												<p className="mt-1 truncate font-mono text-muted-foreground text-xs">
													{identity.bapId}
												</p>
												<p className="mt-1 font-mono text-muted-foreground text-xs">
													{identity.rootAddress}
												</p>
											</div>
										</div>
									</CardContent>
								</Card>
							))
						)}
					</div>

					<DialogFooter>
						<Button
							onClick={() => setDiscoverDialogOpen(false)}
							variant="outline"
						>
							Close
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Create New Identity Dialog */}
			<Dialog
				onOpenChange={(open) => {
					setCreateDialogOpen(open);
					if (!open) {
						setNewIdentityName("");
						setNewIdentityPassword("");
					}
				}}
				open={createDialogOpen}
			>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>Create New Identity</DialogTitle>
						<DialogDescription>
							Create a new BAP identity with a unique BAP ID. You can switch
							between identities at any time.
						</DialogDescription>
					</DialogHeader>

					<div className="space-y-4 py-4">
						<div className="space-y-2">
							<Label htmlFor="identity-name">Identity Name</Label>
							<Input
								autoFocus
								id="identity-name"
								onChange={(e) => setNewIdentityName(e.target.value)}
								onKeyDown={(e) => {
									if (
										e.key === "Enter" &&
										!creating &&
										newIdentityName.trim()
									) {
										// Move focus to password field if name is filled
										document.getElementById("identity-password")?.focus();
									}
								}}
								placeholder="e.g., Work, Personal, Gaming"
								required
								value={newIdentityName}
							/>
							<p className="text-muted-foreground text-xs">
								Choose a descriptive name to help you remember this identity.
							</p>
						</div>

						<div className="space-y-2">
							<Label htmlFor="identity-password">Backup Password</Label>
							<Input
								id="identity-password"
								onChange={(e) => setNewIdentityPassword(e.target.value)}
								onKeyDown={(e) => {
									if (
										e.key === "Enter" &&
										!creating &&
										newIdentityName.trim() &&
										newIdentityPassword
									) {
										handleCreateNewIdentity();
									}
								}}
								placeholder="Enter your backup password"
								required
								type="password"
								value={newIdentityPassword}
							/>
							<p className="text-muted-foreground text-xs">
								Password will be validated against your existing encrypted
								backup
							</p>
						</div>
					</div>

					<DialogFooter>
						<Button
							disabled={creating}
							onClick={() => {
								setCreateDialogOpen(false);
								setNewIdentityName("");
								setNewIdentityPassword("");
							}}
							variant="outline"
						>
							Cancel
						</Button>
						<Button
							disabled={creating || !newIdentityName.trim()}
							onClick={handleCreateNewIdentity}
						>
							{creating ? (
								<>
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									Creating...
								</>
							) : (
								<>
									<Plus className="mr-2 h-4 w-4" />
									Create Identity
								</>
							)}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Password Dialog for Identity Import */}
			<Dialog
				onOpenChange={(open) => {
					setPasswordDialogOpen(open);
					if (!open) {
						setPassword("");
						setPendingIdentity(null);
					}
				}}
				open={passwordDialogOpen}
			>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>Save Identity to Backup</DialogTitle>
						<DialogDescription>
							To save this identity permanently to your backup, please enter
							your backup password. You can skip this and import for the current
							session only.
						</DialogDescription>
					</DialogHeader>

					<div className="space-y-4 py-4">
						{pendingIdentity && (
							<div className="rounded-md bg-muted p-3">
								<p className="font-medium text-sm">{pendingIdentity.name}</p>
								<p className="mt-1 font-mono text-muted-foreground text-xs">
									{pendingIdentity.bapId}
								</p>
							</div>
						)}
						<div className="space-y-2">
							<Label htmlFor="backup-password">
								Backup Password (Optional)
							</Label>
							<Input
								autoFocus
								id="backup-password"
								onChange={(e) => setPassword(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === "Enter" && !importing) {
										handleConfirmImport();
									}
								}}
								placeholder="Enter your backup password"
								type="password"
								value={password}
							/>
							<p className="text-muted-foreground text-xs">
								{password
									? "Identity will be saved permanently to your backup"
									: "Without password, identity will only persist for this session"}
							</p>
						</div>
					</div>

					<DialogFooter>
						<Button
							disabled={importing}
							onClick={() => {
								setPasswordDialogOpen(false);
								setPassword("");
								setPendingIdentity(null);
							}}
							variant="outline"
						>
							Cancel
						</Button>
						<Button disabled={importing} onClick={handleConfirmImport}>
							{importing ? (
								<>
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									Importing...
								</>
							) : (
								"Import Identity"
							)}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Backup Download Confirmation Dialog */}
			<AlertDialog
				onOpenChange={setConfirmBackupDownloadOpen}
				open={confirmBackupDownloadOpen}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							Download Backup to Save Identity
						</AlertDialogTitle>
						<AlertDialogDescription>
							New identity "{newlyCreatedIdentityName}" created successfully!
							<br />
							<br />
							Would you like to download your updated master backup now?
							<br />
							<br />
							<strong>This is important:</strong> Without saving your backup,
							this identity will be lost if you clear your browser data.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel onClick={handleSkipBackupDownload}>
							Skip for Now
						</AlertDialogCancel>
						<AlertDialogAction onClick={handleConfirmBackupDownload}>
							Download Backup
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			{/* Master Backup Password Dialog */}
			<Dialog
				onOpenChange={(open) => {
					setMasterPasswordDialogOpen(open);
					if (!open) {
						setMasterPassword("");
						// If user cancels and we were waiting for download to reload, navigate and reload
						if (shouldReloadAfterDownload) {
							setShouldReloadAfterDownload(false);

							// Navigate to newly created identity before reloading
							if (newlyCreatedIdentityId) {
								router.push(
									`/account/${encodeURIComponent(newlyCreatedIdentityId)}`
								);
								setTimeout(() => {
									window.location.reload();
								}, 100);
							} else {
								setTimeout(() => {
									window.location.reload();
								}, 300);
							}
						}
					}
				}}
				open={masterPasswordDialogOpen}
			>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>Encrypt Master Backup</DialogTitle>
						<DialogDescription>
							Enter a strong password to encrypt your master backup file. This
							backup contains all your identities.
						</DialogDescription>
					</DialogHeader>

					<div className="space-y-4 py-4">
						<div className="space-y-2">
							<Label htmlFor="master-password">Password</Label>
							<Input
								autoFocus
								id="master-password"
								onChange={(e) => setMasterPassword(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === "Enter" && !downloadingMaster) {
										handleDownloadMasterBackup();
									}
								}}
								placeholder="Enter a strong password"
								type="password"
								value={masterPassword}
							/>
							<p className="text-muted-foreground text-xs">
								Remember this password - you'll need it to restore your backup.
							</p>
						</div>
					</div>

					<DialogFooter>
						<Button
							disabled={downloadingMaster}
							onClick={() => {
								setMasterPasswordDialogOpen(false);
								setMasterPassword("");
							}}
							variant="outline"
						>
							Cancel
						</Button>
						<Button
							disabled={downloadingMaster || !masterPassword}
							onClick={handleDownloadMasterBackup}
						>
							{downloadingMaster ? (
								<>
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									Downloading...
								</>
							) : (
								<>
									<Download className="mr-2 h-4 w-4" />
									Download
								</>
							)}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Member Backup Password Dialog */}
			<Dialog
				onOpenChange={(open) => {
					setMemberPasswordDialogOpen(open);
					if (!open) {
						setMemberPassword("");
					}
				}}
				open={memberPasswordDialogOpen}
			>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>Encrypt Member Backup</DialogTitle>
						<DialogDescription>
							Enter a password to encrypt the member backup for "
							{currentIdentity?.name}".
						</DialogDescription>
					</DialogHeader>

					<div className="space-y-4 py-4">
						<div className="space-y-2">
							<Label htmlFor="member-password">Password</Label>
							<Input
								autoFocus
								id="member-password"
								onChange={(e) => setMemberPassword(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === "Enter" && !downloadingMember) {
										handleDownloadMemberBackup();
									}
								}}
								placeholder="Enter a password"
								type="password"
								value={memberPassword}
							/>
							<p className="text-muted-foreground text-xs">
								This backup contains only the private key for this identity.
							</p>
						</div>
					</div>

					<DialogFooter>
						<Button
							disabled={downloadingMember}
							onClick={() => {
								setMemberPasswordDialogOpen(false);
								setMemberPassword("");
							}}
							variant="outline"
						>
							Cancel
						</Button>
						<Button
							disabled={downloadingMember || !memberPassword}
							onClick={handleDownloadMemberBackup}
						>
							{downloadingMember ? (
								<>
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									Downloading...
								</>
							) : (
								<>
									<Download className="mr-2 h-4 w-4" />
									Download
								</>
							)}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
